import { useState } from 'react'
import { Popper, Paper, Box, Typography, Autocomplete, TextField, CircularProgress, Divider, IconButton } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useEditCropContext } from './edit-crop-context'
import { useCropVarieties } from '~/crop/crop-modal/hooks/use-crop-varieties'
import { NumberFieldOrSpinner } from '~/components/number-spinner-field'

/**
 * EditCropPopper
 *
 * Small inline popper anchored below the crop chip when in edit mode.
 * Contains variety autocomplete and count spinner.
 * No ClickAwayListener — user must submit (Check) or stop editing explicitly.
 *
 * @param {HTMLElement|null} anchorEl - The element to anchor to; null = closed
 */
export const EditCropPopper = ({ anchorEl }) => {
  const [collapsed, setCollapsed] = useState(false)

  const {
    editingCropDetailsLoading,
    speciesTemplate,
    variety,
    count,
    successions,
    notes,
    isSubmitting,
    setVariety,
    setCount,
    setSuccessions,
    setNotes,
  } = useEditCropContext()

  const { varieties } = useCropVarieties({ speciesId: speciesTemplate?.id ?? null })

  const open = Boolean(anchorEl)
  const isLoading = editingCropDetailsLoading || isSubmitting

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: 'offset', options: { offset: [7, 4] } }]}
      sx={{ zIndex: 1300 }}
    >
      <Paper
        elevation={4}
        sx={{
          px: 2, py: 1,
          width: collapsed ? 200 : { xs: 300, sm: 400, md: 500 },
          maxHeight: collapsed ? 42 : 600,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.25s ease, max-height 0.25s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Edit Crop
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {editingCropDetailsLoading && <CircularProgress size={14} />}
            <IconButton size="small" onClick={() => setCollapsed(c => !c)}>
              {collapsed ? <Icons.ExpandMore fontSize="small" /> : <Icons.ExpandLess fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 0.5 }}>
          <Divider />

          <Autocomplete
            size="small"
            options={varieties}
            value={variety}
            getOptionLabel={(opt) => opt?.name ?? ''}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            disabled={isLoading || !speciesTemplate}
            renderInput={(params) => (
              <TextField {...params} label="Variety (optional)" placeholder="Select variety" />
            )}
            onChange={(_, newValue) => setVariety(newValue ?? null)}
          />

          <NumberFieldOrSpinner
            label="Plant count"
            min={1}
            value={count}
            onValueChange={setCount}
            disabled={isLoading}
            size="small"
          />

          <NumberFieldOrSpinner
            label="Succession count"
            min={0}
            value={successions}
            onValueChange={setSuccessions}
            disabled={isLoading}
            size="small"
          />

          <TextField
            label="Notes"
            size="small"
            multiline
            minRows={2}
            maxRows={8}
            value={notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isLoading}
            placeholder="Add notes…"
          />
        </Box>
      </Paper>
    </Popper>
  )
}
